/**
 * TaskDetailModal Component
 * 
 * Modal for completing pack tasks within a store visit
 * Shows format dropdowns per format, one image per pack, and notes per pack
 */

import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Package, Loader2, Camera, X, ChevronLeft } from 'lucide-react';
import { usePhotoCapture } from '@/hooks/use-photo-capture';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useUploadQueue } from '@/hooks/useUploadQueue';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { apiFetch } from '@/lib/api';
import { useAuthUser } from '@/hooks/useAuthUser';
import { handleMobileSubmissionResponse } from '@/utils/mobileSubmissionHandler';
import type { PackTask, CompletedTask, CampaignAtStore, Format } from '@/types/store-first';
import { cn } from '@/lib/utils';
import { ModalContextHeader } from '@/components/mobile/ModalContextHeader';

interface TaskDetailModalProps {
  packTask: PackTask | null;
  campaign: CampaignAtStore;
  storeId: number;
  storeName?: string;
  isOpen: boolean;
  onClose: () => void;
  onComplete: (completedTask: CompletedTask) => void;
}

export function TaskDetailModal({
  packTask,
  campaign,
  storeId,
  storeName,
  isOpen,
  onClose,
  onComplete,
  onStoreReadyForSignature
}: TaskDetailModalProps & { onStoreReadyForSignature?: () => void }) {
  // CRITICAL: Prevent editing completed assignments
  const taskStatus = packTask?.status as string | undefined;
  const isCompleted = taskStatus === 'completed' ||
    taskStatus === 'issue' ||
    taskStatus === 'operator_complete' ||
    taskStatus === 'admin_complete' ||
    taskStatus === 'locked';

  // If completed, close immediately and don't render
  useEffect(() => {
    if (isOpen && isCompleted) {
      console.warn('[TaskDetailModal] Attempted to open completion modal for completed assignment', {
        assignmentId: packTask?.assignmentId,
        status: packTask?.status
      });
      onClose();
    }
  }, [isOpen, isCompleted, packTask, onClose]);

  // Don't render if completed
  if (isCompleted) {
    return null;
  }

  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Format delivered quantities (one per format in pack)
  const [formatDelivered, setFormatDelivered] = useState<Record<number, number>>({});
  // Fallback for packs with no explicit formats: one total dropdown
  const [singleDelivered, setSingleDelivered] = useState<number>(packTask?.plannedQuantity ?? 0);

  const { toast } = useToast();
  const { enqueue } = useUploadQueue();
  const isOnline = useNetworkStatus();
  const { user } = useAuthUser();
  const queryClient = useQueryClient();

  const {
    photos,
    isCapturing,
    error: photoError,
    fileInputRef,
    captureFromCamera,
    captureFromFile,
    removePhoto,
    clearPhotos,
    triggerFileInput,
    canAddMore,
    pendingPhoto,
    confirmPendingPhoto,
    rejectPendingPhoto,
  } = usePhotoCapture({ maxPhotos: 1 }); // Only one image per pack

  // Initialize format delivered counts from pack task formats
  useEffect(() => {
    if (packTask) {
      if (packTask.formats.length > 0) {
        const initialDelivered: Record<number, number> = {};
        packTask.formats.forEach(format => {
          initialDelivered[format.formatId] =
            format.deliveredQuantity ?? format.plannedQuantity ?? 0;
        });
        setFormatDelivered(initialDelivered);
      } else {
        setSingleDelivered(packTask.plannedQuantity || 0);
      }
    }
  }, [packTask]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setNotes('');
      clearPhotos();
      if (packTask) {
        if (packTask.formats.length > 0) {
          const initialDelivered: Record<number, number> = {};
          packTask.formats.forEach(format => {
            initialDelivered[format.formatId] =
              format.deliveredQuantity ?? format.plannedQuantity ?? 0;
          });
          setFormatDelivered(initialDelivered);
        } else {
          setSingleDelivered(packTask.plannedQuantity || 0);
        }
      }
    }
  }, [isOpen, packTask, clearPhotos]);

  // Calculate total planned and delivered
  const totalPlanned = packTask?.plannedQuantity || 0;
  const totalDelivered = (packTask?.formats?.length || 0) > 0
    ? Object.values(formatDelivered).reduce((sum, qty) => sum + qty, 0)
    : singleDelivered;
  const requiresDiscrepancyNote = totalDelivered !== totalPlanned;

  // Photo upload mutation - pack-level image
  const uploadPhotoMutation = useMutation({
    mutationFn: async ({ taskId, photo, campaignId, storeId }: { taskId: number; photo: File; campaignId?: number; storeId?: number }) => {
      if (!isOnline || !navigator.onLine) {
        // Queue file for later upload
        await enqueue(photo, `/api/mobile/upload-photo`, {
          taskId: taskId.toString()
        });
        return { queued: true, url: null };
      }

      // Use apiFetch to include auth headers
      // DO NOT set Content-Type header - FormData sets it automatically with boundary
      const formData = new FormData();
      formData.append('taskId', taskId.toString());
      formData.append('photo', photo);

      // Add campaignId and storeId for fallback lookup if taskId doesn't match
      if (campaignId) {
        formData.append('campaignId', campaignId.toString());
      }
      if (storeId) {
        formData.append('storeId', storeId.toString());
      }

      console.log('[TaskDetailModal] Uploading photo:', {
        taskId,
        taskIdType: typeof taskId,
        taskIdString: taskId.toString(),
        campaignId,
        storeId,
        fileName: photo.name,
        fileSize: photo.size,
        fileType: photo.type
      });

      const response = await apiFetch('/api/mobile/upload-photo', {
        method: 'POST',
        body: formData,
        // Explicitly omit Content-Type to let FormData set it
        headers: {},
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText };
        }
        console.error('[TaskDetailModal] Upload failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData,
          taskIdSent: taskId,
          formDataEntries: Array.from(formData.entries()).map(([key, val]) => ({
            key,
            value: val instanceof File ? `File: ${val.name} (${val.size} bytes)` : val
          }))
        });
        throw new Error(errorData.error || errorData.message || `Failed to upload photo: ${response.status}`);
      }

      const data = await response.json();
      console.log('[TaskDetailModal] Upload success:', data);
      return {
        queued: false,
        url: data.secureUrl || data.publicUrl || data.url,
        publicId: data.publicId,
        secureUrl: data.secureUrl
      };
    },
  });


  const handleSubmit = async () => {
    if (!packTask || !campaign.packId) return;

    // Validate required fields
    if (photos.length === 0) {
      toast({
        title: 'Photo Required',
        description: 'Please capture at least one photo for the pack',
        variant: 'destructive',
      });
      return;
    }

    // Validate discrepancy note if needed
    if (requiresDiscrepancyNote && !notes.trim()) {
      toast({
        title: 'Discrepancy Note Required',
        description: 'Please provide a note explaining the difference between planned and delivered quantities',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const fallbackTaskId = (packTask as unknown as { taskId?: number }).taskId;
      // Upload pack-level image
      let imageUrl: string | null = null;
      let uploadedPhotoData: { publicId: string; secureUrl: string } | null = null;

      if (photos.length > 0) {
        try {
          // PackTask can originate from different sources. Prefer native assignmentId but
          // gracefully fall back to taskId if provided by other flows.
          const taskIdToUse = packTask.assignmentId ?? packTask.legacyAssignmentId ?? fallbackTaskId;

          if (!taskIdToUse) {
            console.error('[TaskDetailModal] No taskId found in packTask:', packTask);
            throw new Error('Missing task identifier in assignment data');
          }

          console.log('[TaskDetailModal] Preparing to upload photo with taskId:', taskIdToUse);
          console.log('[TaskDetailModal] Full packTask object:', JSON.stringify(packTask, null, 2));
          console.log('[TaskDetailModal] Task ID check:', {
            assignmentId: packTask.assignmentId,
            taskId: fallbackTaskId,
            using: taskIdToUse,
            packTaskKeys: Object.keys(packTask as unknown as Record<string, unknown>),
            packTaskFull: JSON.stringify(packTask, null, 2)
          });
          const result = await uploadPhotoMutation.mutateAsync({
            taskId: taskIdToUse,
            photo: photos[0].file,
            campaignId: campaign.campaignId,
            storeId: storeId
          });

          if (result.url) {
            imageUrl = result.url;
            // Store photo data for submission (NEW: required for new evidence architecture)
            if (result.publicId && result.secureUrl) {
              uploadedPhotoData = {
                publicId: result.publicId,
                secureUrl: result.secureUrl
              };
              console.log('[TaskDetailModal] Photo uploaded - will include in submission:', uploadedPhotoData);
            }
          } else if (result.queued) {
            // Photo queued - use preview URL temporarily
            imageUrl = photos[0].preview;
          }
        } catch (error) {
          console.error('Error uploading photo:', error);
          toast({
            title: 'Photo Upload Failed',
            description: 'Failed to upload photo. Please try again.',
            variant: 'destructive',
          });
          setIsSubmitting(false);
          return;
        }
      }

      // Persist per-format delivery counts to backend (mobile-submission)
      try {
        const formatEvidence = (packTask.formats || []).map((f) => ({
          formatId: f.formatId,
          expectedCount: f.plannedQuantity || 0,
          actualCount: formatDelivered[f.formatId] || 0,
        }));

        // Extract taskId - same logic as photo upload
        const taskIdToUse = packTask.assignmentId ?? packTask.legacyAssignmentId ?? fallbackTaskId;

        // Build evidence assets array from uploaded photos (NEW: required for new evidence architecture)
        const evidenceAssets = uploadedPhotoData ? [{
          publicId: uploadedPhotoData.publicId,
          secureUrl: uploadedPhotoData.secureUrl,
          assetType: 'image' as const
        }] : [];

        const payload = {
          taskId: taskIdToUse, // ✅ required
          storeId: storeId,
          campaignId: campaign.campaignId,
          postedUnits: totalDelivered,
          evidenceAssets, // ✅ FIXED: Include uploaded photo URLs from storage
          notes: notes?.trim() || undefined,
          formatEvidence,
        };

        const resp = await apiFetch('/api/mobile/mobile-submission', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!resp.ok) {
          const txt = await resp.text().catch(() => '');
          console.error('[TaskDetailModal] mobile-submission failed', resp.status, txt);

          // Handle 409 Conflict (already completed) specially
          if (resp.status === 409) {
            let errorData: any = {};
            try {
              errorData = JSON.parse(txt);
            } catch { }

            // Refresh data and show friendly message
            queryClient.invalidateQueries({ queryKey: ['storeVisits'] });
            queryClient.invalidateQueries({ queryKey: ['completion'] });
            queryClient.invalidateQueries({ queryKey: ['route-overview'] });

            toast({
              title: 'Pack Already Completed',
              description: errorData.error || 'This pack has already been completed.',
              variant: 'default',
            });

            // Close modal gracefully
            onClose();
            return; // Exit early - don't show error toast
          }

          // Handle 400 validation with clear message
          if (resp.status === 400) {
            let errorData: any = {};
            try {
              errorData = JSON.parse(txt);
            } catch { }
            toast({
              title: 'Submission Blocked',
              description: errorData.error || 'Evidence validation failed. Please attach a photo and try again.',
              variant: 'destructive',
            });
            return;
          }

          throw new Error(`Submission failed (${resp.status}): ${txt}`);
        }

        // Parse response and handle query invalidation
        const responseData: any = await resp.json();
        handleMobileSubmissionResponse(
          queryClient,
          responseData,
          user?.id,
          () => {
            // Trigger signature modal when assignment completes
            if (responseData.assignmentCompleted || responseData.packCompleted) {
              const completedStores = responseData.counts?.assignmentStoresCompleted || 0;
              const totalStores = responseData.counts?.assignmentStoresTotal || 0;
              toast({
                title: 'All Stores Completed!',
                description: `${completedStores} of ${totalStores} stores completed. Manager signature required.`,
              });
              // TODO: Open signature modal here if needed
            }
          }
        );

        // ✅ INSTANT SIGNATURE TRIGGER: If this was the last task for the store
        if (responseData.storeReadyForSignature) {
          console.log('[TASK_DETAIL] 🎉 Store ready for signature - triggering modal');
          toast({
            title: '🎉 Store Completed!',
            description: `${storeName || 'Store'} is complete. Manager sign-off required.`,
          });

          // Close the task modal first
          onClose();

          // Trigger signature modal immediately
          if (onStoreReadyForSignature) {
            setTimeout(() => {
              onStoreReadyForSignature();
            }, 500); // Brief delay to allow task modal to close smoothly
          }
        }

        // CRITICAL: Invalidate evidence query so View Evidence shows fresh data
        queryClient.invalidateQueries({
          queryKey: ['assignment-evidence', taskIdToUse]
        });
      } catch (e) {
        console.error('[TaskDetailModal] mobile-submission error', e);

        // Check if it's a 409 error that wasn't caught above
        if (e instanceof Error && e.message.includes('already')) {
          queryClient.invalidateQueries({ queryKey: ['storeVisits'] });
          queryClient.invalidateQueries({ queryKey: ['completion'] });
          toast({
            title: 'Pack Already Completed',
            description: e.message || 'This pack has already been completed.',
            variant: 'default',
          });
          onClose();
          return;
        }

        throw e; // Re-throw other errors to show error toast
      }

      // Create completed task object (pack-level completion)
      // Note: We store one CompletedTask per pack, with format-level delivered quantities
      const completedTask: CompletedTask = {
        assignmentId: packTask.assignmentId ?? packTask.legacyAssignmentId ?? fallbackTaskId ?? 0,
        deliveredQuantity: totalDelivered, // Total delivered across all formats
        status: totalDelivered === totalPlanned ? 'completed' : 'issue',
        imageUrl: imageUrl,
        notes: notes.trim() || null,
        completedAt: new Date(),
      };

      // Call completion callback
      onComplete(completedTask);

      toast({
        title: 'Pack Completed',
        description: `${campaign.packName || campaign.campaignName} marked as ${completedTask.status === 'completed' ? 'completed' : 'completed with issues'}`,
      });

      // Close modal
      onClose();
    } catch (error) {
      console.error('Error completing pack:', error);

      // Don't show error if it's already handled (409 case)
      if (error instanceof Error && error.message.includes('already')) {
        return; // Already handled above
      }

      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to complete pack. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!packTask || !campaign) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2 -ml-2 mb-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              disabled={isSubmitting}
              className="h-10 w-10 rounded-lg"
              aria-label="Go back"
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
          </div>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Complete {campaign.packName || campaign.campaignName}
          </DialogTitle>
          <DialogDescription>
            <ModalContextHeader
              storeName={storeName || 'Unknown Store'}
              campaignName={campaign.campaignName}
              assignmentId={packTask?.assignmentId}
            />
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Pack Info */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm text-gray-600 mb-1">Planned Quantity</div>
            <div className="text-lg font-semibold">{totalPlanned} units</div>
          </div>

          {/* Format Delivery Counts - One dropdown per format */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Format Delivery Count</Label>
            {(() => {
              console.log('[TaskDetailModal] Formats:', packTask?.formats);
              console.log('[TaskDetailModal] Format count:', packTask?.formats?.length);
              return null;
            })()}
            {packTask.formats.length === 0 ? (
              <div className="space-y-2">
                <div className="text-sm text-gray-600">No formats listed. Enter total delivered:</div>
                <div className="flex items-center gap-2 sm:min-w-[150px]">
                  <label htmlFor="single-delivered" className="sr-only">Total delivered units</label>
                  <select
                    id="single-delivered"
                    aria-label="Total delivered units"
                    value={singleDelivered}
                    onChange={(e) => setSingleDelivered(parseInt(e.target.value, 10))}
                    disabled={isSubmitting}
                    className="h-12 w-full min-w-[140px] rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    title="Total delivered units"
                  >
                    {Array.from({ length: totalPlanned + 1 }, (_, i) => i).map((qty) => (
                      <option key={qty} value={qty}>
                        {qty}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {packTask.formats.map((format) => {
                  const delivered = formatDelivered[format.formatId] || 0;
                  const planned = format.plannedQuantity || 0;

                  console.log('[TaskDetailModal] Rendering format:', format.formatId, format.formatName, 'planned:', planned, 'delivered:', delivered);

                  return (
                    <div key={format.formatId} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{format.formatName || `Format ${format.formatId}`}</p>
                        <p className="text-xs text-gray-500">Required: {planned} units</p>
                      </div>
                      <div className="flex items-center gap-2 sm:min-w-[150px]">
                        <label htmlFor={`delivered-${format.formatId}`} className="sr-only">
                          Delivered units for {format.formatName || 'format'}
                        </label>
                        <select
                          id={`delivered-${format.formatId}`}
                          aria-label={`Delivered units for ${format.formatName || 'format'}`}
                          value={delivered}
                          onChange={(e) => {
                            const numValue = parseInt(e.target.value, 10);
                            setFormatDelivered({ ...formatDelivered, [format.formatId]: numValue });
                          }}
                          disabled={isSubmitting}
                          className="h-12 w-full min-w-[140px] rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          title={`Delivered units for ${format.formatName || 'format'}`}
                        >
                          {Array.from({ length: planned + 1 }, (_, i) => i).map((qty) => (
                            <option key={qty} value={qty}>
                              {qty}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Total delivered summary */}
            <div className="mt-3 pt-3 border-t">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Total Delivered</span>
                <span className={cn(
                  "text-lg font-semibold",
                  totalDelivered === totalPlanned ? "text-green-600" : "text-orange-600"
                )}>
                  {totalDelivered} / {totalPlanned}
                </span>
              </div>
            </div>
          </div>

          {/* Notes - Required if delivered ≠ planned */}
          <div className="space-y-2">
            <Label htmlFor="notes">
              Notes {requiresDiscrepancyNote && <span className="text-red-600">*</span>}
            </Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={requiresDiscrepancyNote
                ? "Required: Please explain the difference between planned and delivered quantities..."
                : "Optional: Add any notes about this pack..."
              }
              rows={3}
              disabled={isSubmitting}
            />
            {requiresDiscrepancyNote && !notes.trim() && (
              <p className="text-xs text-red-600">
                Discrepancy note is required when delivered quantity differs from planned
              </p>
            )}
          </div>

          {/* Photo Capture - One image per pack (required) */}
          <div className="space-y-2">
            <Label>Proof Photo <span className="text-red-600">*</span></Label>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={captureFromCamera}
                disabled={!canAddMore || isCapturing || isSubmitting}
                className="flex-1"
              >
                {isCapturing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Capturing...
                  </>
                ) : (
                  <>
                    <Camera className="h-4 w-4 mr-2" />
                    Camera
                  </>
                )}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={triggerFileInput}
                disabled={!canAddMore || isSubmitting}
                className="flex-1"
              >
                Choose File
              </Button>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => captureFromFile(e.target.files)}
                className="hidden"
                aria-label="Upload photo file"
              />
            </div>

            {photoError && (
              <p className="text-sm text-red-600">{photoError}</p>
            )}

            {/* Photo Preview */}
            {photos.length > 0 && (
              <div className="mt-2">
                <div className="relative w-full aspect-square max-w-xs mx-auto">
                  <img
                    src={photos[0].preview}
                    alt="Pack proof photo"
                    className="w-full h-full object-cover rounded-lg border-2 border-gray-300"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 h-8 w-8"
                    onClick={() => removePhoto(photos[0].id)}
                    disabled={isSubmitting}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {photos.length === 0 && (
              <p className="text-xs text-red-600">
                At least one photo is required
              </p>
            )}
          </div>

          {/* Photo Preview/Confirm Dialog */}
          {pendingPhoto && (
            <Dialog open={!!pendingPhoto} onOpenChange={() => rejectPendingPhoto()}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Confirm Photo</DialogTitle>
                  <DialogDescription>
                    Review your photo. Use it or take another?
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="relative w-full aspect-square max-w-md mx-auto">
                    <img
                      src={pendingPhoto.preview}
                      alt="Photo preview"
                      className="w-full h-full object-cover rounded-lg border-2 border-gray-300"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={rejectPendingPhoto}
                      className="flex-1"
                    >
                      Take Another
                    </Button>
                    <Button
                      onClick={confirmPendingPhoto}
                      className="flex-1"
                    >
                      Use This Photo
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 pt-4 pb-[max(env(safe-area-inset-bottom,0px),80px)]">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || photos.length === 0 || (requiresDiscrepancyNote && !notes.trim())}
              className="flex-1"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Package className="h-4 w-4 mr-2" />
                  Complete Pack
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
